/** Authenticated HTTP and the shared write-error contract. */
import { auth, getIdToken } from "@/firebase";
import { decodeWire } from "@/api/wire";
import { useFeedback } from "@/stores/feedback";

const BASE = import.meta.env.VITE_API_URL ?? "";
export interface RequestOptions<T = unknown> {
  /** Authorize this intent before every dispatch, including initial token acquisition. */
  canSend?: () => boolean;
  canRetry?: () => boolean;
  responseType?: "json" | "blob";
  idempotencyKey?: string;
  onConflict?: () => unknown;
  onRetrySuccess?: (result: T) => unknown;
}
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    public detail?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}
function errorDetail(
  value: unknown,
  fallback: string,
): { message: string; code?: string; detail: unknown } {
  if (!value || typeof value !== "object") return { message: fallback, detail: value };
  const envelope = value as Record<string, unknown>;
  const detail = envelope.detail ?? value;
  const body =
    detail && typeof detail === "object" && !Array.isArray(detail)
      ? (detail as Record<string, unknown>)
      : envelope;
  const code =
    typeof body.code === "string"
      ? body.code
      : typeof envelope.code === "string"
        ? envelope.code
        : undefined;
  const message =
    typeof detail === "string"
      ? detail
      : typeof body.detail === "string"
        ? body.detail
        : Array.isArray(detail)
          ? detail
              .map((item) => (typeof item?.msg === "string" ? item.msg : "Invalid value"))
              .join("; ")
          : fallback;
  return { message, code, detail };
}
/** Separate role clients keep link credentials out of account administration. */
export function createApiClient(
  tokenProvider: () => Promise<string | null>,
  currentSession?: () => string | null,
) {
  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    options: RequestOptions<T> = {},
  ): Promise<T> {
    // This key belongs to the submission, including retries offered by the toast.
    const key = options.idempotencyKey ?? (method === "POST" ? crypto.randomUUID() : undefined);
    const sessionAtSubmission = currentSession?.();
    const feedbackKey = `api:${method}:${path}`;
    const execute = async (retry = false): Promise<T> => {
      const checkCanSend = () => {
        if (options.canSend && !options.canSend())
          throw new ApiError(409, "This action is no longer current", "stale_send");
      };
      const checkSession = () => {
        if (
          currentSession &&
          (sessionAtSubmission == null || currentSession() !== sessionAtSubmission)
        ) {
          throw new ApiError(
            409,
            "This session changed. Reopen this screen before retrying.",
            "stale_session",
          );
        }
      };
      const checkRetry = () => {
        checkSession();
        if (retry && options.canRetry && !options.canRetry()) {
          const feedback = useFeedback.getState();
          feedback.toasts
            .filter((toast) => toast.key === feedbackKey)
            .forEach((toast) => feedback.dismiss(toast.id));
          feedback.show({
            key: `${feedbackKey}:stale`,
            kind: "info",
            message: "This action is no longer current. Its retry was cancelled.",
          });
          throw new ApiError(409, "This action is no longer current", "stale_retry");
        }
        checkCanSend();
      };
      try {
        checkRetry();
        const token = await tokenProvider();
        checkRetry();
        const multipart = body instanceof FormData;
        const headers: Record<string, string> = multipart
          ? {}
          : { "Content-Type": "application/json" };
        if (token) headers.Authorization = `Bearer ${token}`;
        if (key) headers["Idempotency-Key"] = key;
        const encodedBody =
          body === undefined ? undefined : multipart ? body : JSON.stringify(body);
        checkCanSend();
        const response = await fetch(`${BASE}${path}`, {
          method,
          headers,
          body: encodedBody,
        });
        checkSession();
        if (!response.ok) {
          const value = await response.json().catch(() => null);
          checkSession();
          const error = errorDetail(value, response.statusText || "Request failed");
          throw new ApiError(response.status, error.message, error.code, error.detail);
        }
        const result =
          response.status === 204
            ? (undefined as T)
            : options.responseType === "blob"
              ? ((await response.blob()) as T)
              : (decodeWire(await response.json()) as T);
        checkSession();
        if (retry) {
          await Promise.resolve()
            .then(() => {
              checkSession();
              return options.onRetrySuccess?.(result);
            })
            .catch((cause) => {
              checkSession();
              if (cause instanceof ApiError && cause.code === "stale_session") throw cause;
              useFeedback.getState().show({
                key: `${feedbackKey}:refresh`,
                kind: "info",
                message: "Saved. Refresh this screen to see the latest data.",
              });
            });
          checkSession();
          if (!options.onRetrySuccess) {
            useFeedback.getState().show({
              key: `${feedbackKey}:refresh`,
              kind: "info",
              message: "Saved. Reload this screen to see your change.",
              actionLabel: "Reload",
              action: () => window.location.reload(),
              durationMs: 15_000,
            });
          }
        }
        return result;
      } catch (cause) {
        checkSession();
        const error =
          cause instanceof ApiError
            ? cause
            : new ApiError(0, "Connection lost. Retry your change.");
        if (
          error.code === "stale_retry" ||
          error.code === "stale_send" ||
          error.code === "stale_session"
        )
          throw error;
        if (error.status === 409)
          await Promise.resolve()
            .then(() => {
              checkSession();
              return options.onConflict?.();
            })
            .catch((cause) => {
              checkSession();
              if (cause instanceof ApiError && cause.code === "stale_session") throw cause;
            });
        checkSession();
        if (method !== "GET" && error.status !== 400 && error.status !== 422) {
          useFeedback.getState().show({
            key: feedbackKey,
            kind: "error",
            message: error.message,
            ...(error.status === 403 && error.code === "plan_required" ? { href: "/upgrade" } : {}),
            ...(error.status === 0 || error.status >= 500
              ? { actionLabel: "Retry", action: () => execute(true) }
              : {}),
          });
        }
        throw error;
      }
    };
    return execute();
  }

  return {
    download: (path: string) => request<Blob>("GET", path, undefined, { responseType: "blob" }),
    get: <T>(path: string) => request<T>("GET", path),
    post: <T>(path: string, body?: unknown, options?: RequestOptions<T>) =>
      request<T>("POST", path, body, options),
    put: <T>(path: string, body?: unknown, options?: RequestOptions<T>) =>
      request<T>("PUT", path, body, options),
    delete: <T>(path: string, options?: RequestOptions<T>) =>
      request<T>("DELETE", path, undefined, options),
    upload: <T>(path: string, file: File, options?: RequestOptions<T>) => {
      const form = new FormData();
      form.append("file", file);
      return request<T>("POST", path, form, options);
    },
  };
}

export const api = createApiClient(
  () => getIdToken(),
  () => {
    const uid = auth?.currentUser?.uid;
    return uid ? `account:${uid}` : "anonymous";
  },
);
